import * as THREE from 'three';
import { Engine } from './core/Engine.js';
import { createInputSystem, createInputFlushSystem } from './core/Input.js';
import { getShot, listShots } from './core/Shots.js';

import { createMaterialSystem } from './world/Materials.js';
import { createLevelSystem } from './world/Level.js';
import { createPhysicsSystem } from './physics/Physics.js';
import { createSkySystem } from './world/Sky.js';
import { createLightingSystem } from './world/Lighting.js';
import { createPlayerSystem } from './player/Player.js';
import { createWeaponSystem } from './weapons/Weapons.js';
import { createAISystem } from './ai/AI.js';
import { createFXSystem } from './fx/FX.js';
import { createAudioSystem } from './audio/Audio.js';
import { createHUDSystem } from './ui/HUD.js';
import { createRenderSystem } from './render/PostFX.js';
import { createAutoQualitySystem } from './core/AutoQuality.js';

const canvas = document.getElementById('gl');
const errors = [];

const params = new URLSearchParams(location.search);
// Interactive sessions start one notch down and let AutoQuality climb — first
// impressions on a weak GPU matter more than the first six seconds on a strong
// one. A preset AutoQuality settled on in a previous visit wins outright: the
// walk down from 'high' re-links every shader program per step, and there is no
// reason to make a returning player sit through it twice.
// The capture harness (?headless) keeps the fixed ultra look.
let savedQ = null;
try { savedQ = localStorage.getItem('blackout.aq.preset'); } catch { /* private mode */ }
if (!['potato', 'low', 'medium', 'high', 'ultra'].includes(savedQ)) savedQ = null;
const config = {
  quality: params.get('q') || (params.has('headless') ? 'ultra' : (savedQ || 'high')),
  maxPixelRatio: Number(params.get('dpr') || 1.5),
  dprLocked: params.has('dpr'),
  headless: params.has('headless'),
  debug: params.has('debug'),
  autoQuality: !params.has('q') && !params.has('headless'),
};

const engine = new Engine(canvas, config);
window.THREE = THREE;

const inputSys = createInputSystem(canvas);
engine.add(inputSys);

/** Add a system, isolating a construction failure so the rest of the game still boots. */
function safeAdd(label, factory) {
  try {
    const sys = factory();
    if (sys) engine.add(sys);
    return sys;
  } catch (err) {
    console.error(`[boot] ${label} failed:`, err);
    errors.push({ stage: 'construct', label, message: String(err?.stack || err) });
    return null;
  }
}

safeAdd('materials', () => createMaterialSystem());
safeAdd('level', () => createLevelSystem());
safeAdd('physics', () => createPhysicsSystem());
safeAdd('sky', () => createSkySystem());
safeAdd('lighting', () => createLightingSystem());
safeAdd('player', () => createPlayerSystem());
safeAdd('weapons', () => createWeaponSystem());
safeAdd('ai', () => createAISystem());
safeAdd('fx', () => createFXSystem());
safeAdd('audio', () => createAudioSystem());
safeAdd('ui', () => createHUDSystem());
safeAdd('render', () => createRenderSystem());
safeAdd('autoquality', () => createAutoQualitySystem());

engine.add(createInputFlushSystem(inputSys));

// Per-system init isolation: one broken module must not blank the screen.
const bootTimings = [];

/**
 * Canonical visibility, snapshotted once after init.
 *
 * Shots hide and show scene objects to stage themselves and do not always put them
 * back — `ui_hud` measured 982 k triangles when it followed `char_squad` (which hides
 * the level) and 4 150 k when it ran first. Same shot, same build, 4× the geometry.
 * Restoring this snapshot before every shot makes a shot's framing independent of
 * whatever ran before it, without needing each team to cooperate.
 *
 * Objects created after boot are not in the snapshot and are left alone — they belong
 * to whichever shot made them, and that shot owns tearing them down.
 */
const visibilitySnapshot = new Map();
function snapshotVisibility() {
  visibilitySnapshot.clear();
  for (const root of [engine.scene, engine.viewScene]) {
    root.traverse((o) => visibilitySnapshot.set(o, { visible: o.visible, parent: o.parent }));
  }
}
function restoreVisibility() {
  for (const [o, s] of visibilitySnapshot) {
    if (o.visible !== s.visible) o.visible = s.visible;
    // A shot that detaches geometry instead of hiding it cannot be undone by the
    // visible flag alone — re-parent it too, or the next shot renders a stripped scene.
    if (s.parent && o.parent !== s.parent) s.parent.add(o);
  }
  // Anything added to the world scene since boot belongs to some earlier shot's staging —
  // character spawns, FX stages, probe rigs, material showcases. Left alone they
  // accumulate: `light_shafts` measured 460 calls in isolation and 724 in a 92-shot run.
  // Hide them; a shot that wants its own props runs after this and re-shows them.
  //
  // `viewScene` is deliberately exempt. It contains only the weapon viewmodel, whose
  // meshes are built lazily on equip() and so are not in the boot snapshot — hiding them
  // made every `wpn_*` shot render a world frame with no weapon in it. That was a
  // regression this rule introduced, caught by the materials team.
  engine.scene.traverse((o) => {
    if (o !== engine.scene && !visibilitySnapshot.has(o) && o.visible) o.visible = false;
  });
}
engine.init = async function isolatedInit() {
  for (const s of engine.systems) {
    if (!s.init) continue;
    const t0 = performance.now();
    try {
      await s.init(engine.ctx);
    } catch (err) {
      console.error(`[init] ${s.name} failed:`, err);
      errors.push({ stage: 'init', label: s.name, message: String(err?.stack || err) });
    }
    bootTimings.push({ system: s.name, ms: Math.round(performance.now() - t0) });
  }
  engine._renderOwner = engine.systems.find((s) => s.ownsRender) || null;
  snapshotVisibility(); // the canonical state every shot is restored to
  engine._onResize();
};

// Per-system tick isolation, with a one-shot error report per system.
const reported = new Set();
function guard(sys, method, args) {
  try {
    sys[method](...args);
  } catch (err) {
    if (!reported.has(sys.name + method)) {
      reported.add(sys.name + method);
      console.error(`[${method}] ${sys.name} failed:`, err);
      errors.push({ stage: method, label: sys.name, message: String(err?.stack || err) });
    }
  }
}
for (const s of engine.systems) {
  for (const m of ['fixed', 'update', 'render', 'resize']) {
    if (typeof s[m] === 'function') {
      const orig = s[m].bind(s);
      s[m] = (...a) => {
        try { orig(...a); } catch (err) {
          if (!reported.has(s.name + m)) {
            reported.add(s.name + m);
            console.error(`[${m}] ${s.name} failed:`, err);
            errors.push({ stage: m, label: s.name, message: String(err?.stack || err) });
          }
        }
      };
    }
  }
}

const ready = (async () => {
  await engine.init();
  engine.start();
})();

/* ------------------------------------------------------------------ *
 * Debug / capture API (see CONTRACTS.md)
 * ------------------------------------------------------------------ */
window.__GAME__ = {
  ready,
  engine,
  ctx: engine.ctx,
  errors,
  bootTimings,
  THREE,
  listShots,
  setDeterministic(on) {
    engine.deterministic = !!on;
    if (on) engine.accumulator = 0;
  },
  step(dt = 1 / 60, count = 1) {
    for (let i = 0; i < count; i++) engine.tick(dt);
  },
  /**
   * Clear transient, shot-scoped state so every capture is independent.
   *
   * Without this a 92-shot run accumulates: the FX shots leave smoke, decals and
   * particles standing (FX only ages when the engine ticks, and the harness only ticks
   * during a shot), so `ui_hud` was being captured wearing every preceding explosion —
   * 707 draw calls against its own 348. `ui_damage` likewise left a red vignette and a
   * re-shown HUD burned into the next eight frames.
   *
   * Every call is optional and defensive: a system that has not shipped a reset hook
   * simply does not participate. Systems own their own reset; the harness only asks.
   */
  resetTransient() {
    const ctx = engine.ctx;
    const tryCall = (fn, ...args) => { try { fn?.(...args); } catch { /* a missing hook is not a failure */ } };
    restoreVisibility();

    /* --- camera and player: the state that made char_squad render an empty wall --- */
    const spawn = ctx.level?.spawnPoints?.[0];
    // `place()` and `freeze()` live on the player *system*, not on the published
    // `ctx.player` data object — shots reach them via `ctx.get('player')`. Calling
    // `ctx.player.freeze?.()` is a silent no-op, which is exactly why an earlier version
    // of this reset moved the camera by 0 mm and `ui_hud` kept rendering whatever the
    // previous shot had staged.
    const playerSys = ctx.get?.('player');
    try {
      // While frozen the player's update() returns early and its rig pins the camera,
      // so unfreezing has to come first or nothing below takes effect.
      playerSys?.freeze?.(false);
      if (spawn && playerSys?.place) {
        const eye = ctx.player?.eyeHeight ?? 1.65;
        playerSys.place({ x: spawn.x, y: spawn.y - eye, z: spawn.z }, 0, 0);
      } else if (spawn && ctx.player?.teleport) {
        ctx.player.teleport(spawn);
      }
      ctx.player?.velocity?.set?.(0, 0, 0);
      if (ctx.player && typeof ctx.player.yaw === 'number') ctx.player.yaw = 0;
      if (ctx.player && typeof ctx.player.pitch === 'number') ctx.player.pitch = 0;
      if (ctx.player) ctx.player.health = ctx.player.maxHealth ?? ctx.player.health;
    } catch { /* optional */ }
    // Layer masks and scene-wide material overrides survive a camera move and silently
    // change what a later shot draws. Both are cheap to restore and easy to forget.
    for (const s of [ctx.scene, ctx.viewScene]) { s.overrideMaterial = null; }
    ctx.camera.layers.enableAll();
    ctx.viewCamera.layers.enableAll();

    const cam = ctx.camera;
    cam.fov = 80; cam.near = 0.02; cam.far = 2000;
    cam.rotation.set(0, 0, 0);
    if (spawn) cam.position.copy(spawn); else cam.position.set(0, 1.65, 0);
    cam.updateProjectionMatrix(); cam.updateMatrixWorld(true);
    ctx.viewCamera.position.copy(cam.position);
    ctx.viewCamera.quaternion.copy(cam.quaternion);
    ctx.viewCamera.updateMatrixWorld(true);

    /* --- viewmodel: restored to its boot state, not forced hidden.
       Forcing it off meant every first-person shot had to opt back in, and none of them
       did — they were written when it was simply always there. Third-person shots that
       need it gone already hide it themselves. --- */
    const vm = ctx.weapons?.viewmodelRoot;
    const vmBoot = vm ? visibilitySnapshot.get(vm) : null;
    if (vm && vmBoot) vm.visible = vmBoot.visible;

    /* --- everything else transient --- */
    tryCall(ctx.fx?.reset?.bind(ctx.fx));
    tryCall(ctx.render?.damageVignette?.bind(ctx.render), 0);
    tryCall(ctx.render?.setDamageVignette?.bind(ctx.render), 0);
    tryCall(ctx.render?.shake?.bind(ctx.render), 0, 0);
    tryCall(ctx.render?.resetHistory?.bind(ctx.render));
    tryCall(ctx.physics?.clearBodies?.bind(ctx.physics));
    tryCall(ctx.physics?.setDebug?.bind(ctx.physics), false);
    tryCall(ctx.ai?.resetFight?.bind(ctx.ai));
    tryCall(ctx.hud?.reset?.bind(ctx.hud));
    const root = document.getElementById('ui-root');
    if (root) root.style.display = 'none'; // shots that want the HUD re-enable it themselves
  },

  setShot(name) {
    const shot = getShot(name);
    if (!shot) throw new Error(`Unknown shot "${name}". Have: ${listShots().map((s) => s.name).join(', ')}`);
    shot.fn(engine.ctx);
    return shot.settle;
  },

  /**
   * Is the thing this shot is named after actually on screen?
   *
   * The harness previously gated on stability — draw calls, error counts, resource
   * growth — and a shot whose camera ends up inside a kerb is perfectly stable at 290
   * draw calls while photographing nothing. `char_squad` was signed off as fixed on
   * exactly that basis, and `char_closeup` lost its subject entirely between builds with
   * no gate noticing. A harness that measures stability instead of content makes a build
   * converge on being reproducibly wrong.
   *
   * This renders the declared subject alone into a small offscreen target and reports the
   * fraction of the frame it covers. Zero means the shot is not showing its subject,
   * whatever else the numbers say.
   *
   * @param {'characters'|'weapons'|'materials'|'ai'|'fx'} kind
   * @returns {{kind:string, found:number, coverage:number, reason?:string}}
   */
  subjectCoverage(kind) {
    const ctx = engine.ctx;
    const roots = [];
    const push = (o) => { if (o && o.isObject3D) roots.push(o); };

    switch (kind) {
      case 'weapons': push(ctx.weapons?.viewmodelRoot); break;
      case 'characters': {
        const list = ctx.characters?.all || ctx.characters?.list || ctx.characters?.spawned;
        if (Array.isArray(list)) for (const c of list) push(c?.root || c);
        // Fall back to a name scan; teams do not all publish a registry.
        if (!roots.length) {
          for (const s of [engine.scene, engine.viewScene]) {
            s.traverse((o) => { if (o.isSkinnedMesh) push(o); });
          }
        }
        break;
      }
      case 'ai': {
        const ents = ctx.ai?.entities || ctx.ai?.agents;
        if (Array.isArray(ents)) for (const e of ents) push(e?.character?.root || e?.root || e?.mesh);
        if (!roots.length) engine.scene.traverse((o) => { if (o.isSkinnedMesh) push(o); });
        break;
      }
      case 'materials': {
        engine.scene.traverse((o) => {
          if (/showcase|mat[_-]?stage|sphere[_-]?grid/i.test(o.name || '')) push(o);
        });
        break;
      }
      case 'fx': push(ctx.fx?.root); break;
      default: return { kind, found: 0, coverage: 0, reason: `unknown subject kind "${kind}"` };
    }

    if (!roots.length) return { kind, found: 0, coverage: 0, reason: 'no subject objects found in the scene' };

    /*
     * Render the FULL scene with the subject flat-tinted, and count the tint.
     *
     * The first version of this rendered the subject *alone*, which answers "is it in the
     * frustum" — not "is it visible". `char_hero` passed at 10.18 % coverage while the
     * frame contained a kerb and a ground plane, because the soldier was in front of the
     * camera and behind a wall. Tinting inside the real render lets occluders occlude.
     *
     * Materials are swapped rather than objects hidden, so shadows, transforms and every
     * other object stay exactly as the shot staged them.
     */
    const KEY = 0x00ff00;
    const tint = new THREE.MeshBasicMaterial({ color: KEY, fog: false, toneMapped: false });
    const swapped = [];
    for (const r of roots) {
      r.traverse((o) => {
        if (o.isMesh || o.isSkinnedMesh || o.isPoints || o.isLine) {
          swapped.push([o, o.material]);
          o.material = tint;
        }
      });
    }
    if (!swapped.length) { tint.dispose(); return { kind, found: roots.length, coverage: 0, reason: 'subject has no drawable meshes' }; }

    const W = 320, H = 180;
    const rt = new THREE.WebGLRenderTarget(W, H, { depthBuffer: true });
    const renderer = ctx.renderer;
    const prevTarget = renderer.getRenderTarget();
    const prevClear = new THREE.Color();
    renderer.getClearColor(prevClear);
    const prevAlpha = renderer.getClearAlpha();
    const prevAuto = renderer.autoClear;
    let covered = 0;
    try {
      renderer.setRenderTarget(rt);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, true, true);
      renderer.autoClear = false;
      renderer.render(engine.scene, ctx.camera);
      renderer.clearDepth();
      renderer.render(engine.viewScene, ctx.viewCamera);
      const buf = new Uint8Array(W * H * 4);
      renderer.readRenderTargetPixels(rt, 0, 0, W, H, buf);
      // The tint is unlit and untonemapped, so it lands near pure green.
      for (let i = 0; i < buf.length; i += 4) {
        if (buf[i + 1] > 180 && buf[i] < 90 && buf[i + 2] < 90) covered++;
      }
    } finally {
      renderer.autoClear = prevAuto;
      renderer.setClearColor(prevClear, prevAlpha);
      renderer.setRenderTarget(prevTarget);
      rt.dispose();
      for (const [o, m] of swapped) o.material = m;
      tint.dispose();
    }
    return { kind, found: roots.length, meshes: swapped.length, coverage: covered / (W * H) };
  },
  hideHud(on) {
    const root = document.getElementById('ui-root');
    if (root) root.style.display = on ? 'none' : '';
  },
  stats() {
    const info = engine.renderer.info;
    return {
      calls: info.render.calls,
      triangles: info.render.triangles,
      programs: info.programs?.length ?? 0,
      textures: info.memory.textures,
      geometries: info.memory.geometries,
      frame: engine.frame,
    };
  },
};

ready.then(() => {
  window.__READY__ = true;
  document.body.dataset.ready = '1';
});
